import {clearInput} from '../util/ui';
import {renderOrderNotice, requestCartCalculation} from '../payment/order';
import {store} from '../store';
import {Feature, startModalLoading, stopModalLoading} from '../reducers/environmentReducer';
import {eventClick} from '../util/dom';
import {$qsAll, $qs} from '../../../@shared/ts/dom';
import {FeatureFlag} from '../../../@type/features';
import {type Maybe} from '../../../@shared/ts/maybe';
import {getLocaleText} from '../util/translation';
import {getErrorString} from '../../../@shared/ts/error';

export function initCouponInput() {
	if (!Feature.enabled(FeatureFlag.COUPON_INPUT)) {
		return;
	}

	displayCouponFeature();

	handleCouponInputEvents();
	handleCouponRemoval();
}

/**
 * Shows the coupon inputs. By default the coupon feature is not displayed.
 */
function displayCouponFeature() {
	for (const $form of $qsAll('.coupon-code-option')) {
		$form.classList.remove('hide');
	}

	$qs('#coupon-code-section')?.classList.remove('hide');
}

function handleCouponInputEvents() {
	// For submitting coupon input
	for (const $form of $qsAll<HTMLFormElement>('form.wc-coupon-code')) {
		$form.addEventListener('submit', async event => {
			event.preventDefault();

			const data = new FormData((event.target as HTMLFormElement) ?? undefined);
			const code = (data.get('coupon_code') as string)?.trim() ?? '';

			startCouponLoading();

			await applyCoupon(code);
			await requestCartCalculation();

			stopCouponLoading();
		});
	}

	// For opening coupon input
	for (const $openCoupon of $qsAll('.coupon-code-option')) {
		$openCoupon.addEventListener('click', showCouponInput);
		$openCoupon.addEventListener('keypress', showCouponInput);
	}
}

function handleCouponRemoval() {
	$qsAll('#pp-summary-lines-body, #pp-summary-lines-body-mobile', $removeButtons => {
		$removeButtons.addEventListener('click', async (event: Event) => {
			const $target = event.target as HTMLElement;
			if (!$target) {
				return;
			}

			const $removeButton = $target.closest<HTMLButtonElement>('.pp-coupon-remove-button[data-coupon]');
			if (!$removeButton) {
				return;
			}

			const {coupon} = $removeButton.dataset;
			if (!coupon) {
				return;
			}

			store.dispatch(startModalLoading());

			await removeCoupon(coupon);
			await requestCartCalculation();

			store.dispatch(stopModalLoading());
		});
	});
}

async function applyCoupon(code: string): Promise<void> {
	const applyCouponURL = Feature.metadata<string>(FeatureFlag.COUPON_INPUT, 'apply_coupon_url');
	const applyCouponNonce = Feature.dynamicMetadata<string>(FeatureFlag.COUPON_INPUT, 'apply_coupon_nonce');

	if (!applyCouponURL || !applyCouponNonce) {
		renderOrderNotice(getLocaleText('Something went wrong. Please try again.'));
		return;
	}

	const formData = new FormData();
	formData.append('security', applyCouponNonce);
	formData.append('coupon_code', code);

	const {error, result} = await fetch(applyCouponURL, {
		method: 'POST',
		credentials: 'include',
		body: formData,
	})
		.then(async response => response.text())
		.then(data => ({result: data}))
		.catch((error: unknown) => ({error})) as Maybe<string>;

	if (error || !result) {
		const errorMessage = getErrorString(error) || getLocaleText('Something went wrong. Please try again.');
		renderOrderNotice(errorMessage);
		return;
	}

	if (result.includes('woocommerce-error')) {
		renderOrderNotice(result);
		return;
	}

	renderOrderNotice(result);
	hideCouponInput();
}

async function removeCoupon(code: string): Promise<boolean> {
	const removeCouponURL = Feature.metadata<string>(FeatureFlag.COUPON_INPUT, 'remove_coupon_url');
	const removeCouponNonce = Feature.dynamicMetadata<string>(FeatureFlag.COUPON_INPUT, 'remove_coupon_nonce');

	if (!removeCouponNonce || !removeCouponURL) {
		renderOrderNotice(getLocaleText('Something went wrong. Please try again.'));
		return false;
	}

	const formData = new FormData();
	formData.append('security', removeCouponNonce);
	formData.append('coupon', code);

	const {error, result} = await fetch(removeCouponURL, {
		method: 'POST',
		credentials: 'include',
		body: formData,
	})
		.then(async response => response.text())
		.then(data => ({result: data}))
		.catch((error: unknown) => ({error})) as Maybe<string>;

	if (error || !result) {
		const errorMessage = getErrorString(error) || getLocaleText('Something went wrong. Please try again.');
		renderOrderNotice(errorMessage);
		return false;
	}

	if (result.includes('woocommerce-error')) {
		renderOrderNotice(result);
		return false;
	}

	renderOrderNotice(result);
	return true;
}

function startCouponLoading() {
	store.dispatch(startModalLoading());

	$qsAll('form.wc-coupon-code input[type="submit"]', (element: HTMLInputElement) => {
		element.disabled = true;
	});

	for (const $spinner of $qsAll('.wc-coupon-spinner')) {
		$spinner.classList.remove('hide');
	}

	// Replace right border of wc-coupon-code-input with spinner
	for (const $border of $qsAll('.wc-coupon-code-input')) {
		$border.classList.add('remove-right-border');
	}

	for (const $applyButton of $qsAll<HTMLInputElement>('.wc-coupon-code-apply')) {
		$applyButton.disabled = true;
	}
}

function stopCouponLoading() {
	store.dispatch(stopModalLoading());

	$qsAll('form.wc-coupon-code input[type="submit"]', (element: HTMLInputElement) => {
		element.disabled = false;
	});

	for (const $spinner of $qsAll('.wc-coupon-spinner')) {
		$spinner.classList.add('hide');
	}

	for (const $border of $qsAll('.wc-coupon-code-input')) {
		$border.classList.remove('remove-right-border');
	}

	for (const $applyButton of $qsAll<HTMLInputElement>('.wc-coupon-code-apply')) {
		$applyButton.disabled = false;
	}
}

function showCouponInput(event: MouseEvent | KeyboardEvent) {
	if (!eventClick(event)) {
		return;
	}

	for (const $coupon of $qsAll('form.wc-coupon-code')) {
		$coupon.classList.remove('hide');
	}

	for (const $option of $qsAll('.coupon-code-option')) {
		$option.classList.add('hide');
	}

	$qsAll('.pp-dropdown', ($dd: HTMLElement) => {
		$dd?.classList.add('shorten');
	});

	$qs('#pp-modal-content')?.addEventListener('mousedown', detectExitTap);
}

function hideCouponInput() {
	for (const $coupon of $qsAll('form.wc-coupon-code')) {
		$coupon.classList.add('hide');
	}

	for (const $option of $qsAll('.coupon-code-option')) {
		$option.classList.remove('hide');
	}

	for (const $invalid of $qsAll('.wc-invalid-coupon')) {
		$invalid.classList.add('hide');
	}

	$qsAll('.pp-dropdown', ($dd: HTMLElement) => {
		$dd?.classList.remove('shorten');
	});

	$qs('#pp-modal-content')?.removeEventListener('mousedown', detectExitTap);
	clearInput('.wc-coupon-code-input');
}

function detectExitTap(e: Event) {
	for (const $el of $qsAll('form.wc-coupon-code')) {
		if ($el.contains(e.target as HTMLElement)) {
			return;
		}
	}

	hideCouponInput();
}
